/**
 * Text helpers for shell features on the ProseMirror editor (#249):
 * thesaurus word pick + replace (`EditorTextPort`) and plain-text offset
 * jumps. Positions are ProseMirror positions; the shell treats them as opaque.
 */

import type { EditorState } from "prosemirror-state";
import { TextSelection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import { nothing, type Maybe } from "../../lib/maybe";
import {
  projectPlainText,
  textOffsetToDocPos,
} from "../../lib/zwdoc/plainText";
import type { EditorTextPort, EditorTextRange } from "../port";
import {
  trimmedSelection,
  wordAround,
  type EditorWordRange,
} from "../wordRange";

/** One char per position, so offsets in this string map 1:1 onto the textblock. */
const LEAF_PLACEHOLDER = "\uFFFC";

/** Selection (trimmed) or the word around the caret, within one textblock. */
export function proseWordOrSelection(
  state: EditorState,
): Maybe<EditorWordRange> {
  const { from, to, empty, $from, $to } = state.selection;
  if ($from.parent.isTextblock === false || $from.parent !== $to.parent)
    return nothing;
  if (empty === false) {
    return trimmedSelection(
      state.doc.textBetween(from, to, undefined, LEAF_PLACEHOLDER),
      from,
    );
  }
  const start = $from.start();
  const text = $from.parent.textBetween(
    0,
    $from.parent.content.size,
    undefined,
    LEAF_PLACEHOLDER,
  );
  return wordAround(text, $from.parentOffset, start);
}

/** `EditorTextPort` over a ProseMirror view (thesaurus replace). */
export function proseEditorTextPort(
  getView: () => EditorView | null,
  editable: () => boolean,
): EditorTextPort {
  return {
    replaceExpectedRange(range: EditorTextRange, text: string): boolean {
      const view = getView();
      if (view === null || editable() === false) return false;
      const { from, to, expected } = range;
      const size = view.state.doc.content.size;
      if (from < 0 || to > size || from > to) return false;
      if (
        view.state.doc.textBetween(from, to, undefined, LEAF_PLACEHOLDER) !==
        expected
      ) {
        return false;
      }
      const tr = view.state.tr.insertText(text, from, to);
      tr.setSelection(TextSelection.create(tr.doc, from + text.length));
      view.dispatch(tr.scrollIntoView());
      view.focus();
      return true;
    },
  };
}

/** Doc position for a plain-text projection offset (occurrences, search). */
export function docPosForTextOffset(
  state: EditorState,
  offset: number,
): number {
  return textOffsetToDocPos(projectPlainText(state.doc), offset);
}
